import { FloatingFocusManager, FloatingList, autoUpdate, flip, useClick, useFloating, useInteractions, useListNavigation, useRole } from '@floating-ui/react';
import { ICONS } from '@frontend/icons';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LuChevronDown, LuChevronUp, LuX } from 'react-icons/lu';

import { SelectContext, SelectContextValue } from './select';
import { SingleOption } from './select-types';

const SCROLL_TOLLERANCE = 4;
const ID = 'multi-select-input';
interface MultiSelectProps<T> {
    id?: string;
    placeholder?: string;
    onChange?: (value: string) => void;
    onSelect?: (option: SingleOption<T> | null) => void;
    onScrollToBottom?: () => void;
    value?: string[];
    isClearable?: boolean;
    children: ReactNode;
    onRemove: (index: number) => void;
    searchValue?: string;

    optionsLoading?: boolean;
    onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
    onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
    disabled?: boolean;
    isOpenOverwrite?: boolean;
    inputRefCallback?: (ref: React.RefObject<HTMLInputElement | null>) => void;
    disableSearch?: boolean;
}

export function MultiSelect<T>(props: MultiSelectProps<T>) {
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    useEffect(() => {
        if (inputRef && props.inputRefCallback) props.inputRefCallback(inputRef);
    }, [inputRef]);
    useEffect(() => {
        if (isOpen) inputRef.current?.focus();
    }, [isOpen]);

    const updateIsOpen = (value: boolean) => {
        if (props.isOpenOverwrite === undefined) setIsOpen(value);
        else setIsOpen(props.isOpenOverwrite);
    };

    useEffect(() => {
        if (props.isOpenOverwrite !== undefined) setIsOpen(props.isOpenOverwrite);
        else setIsOpen(false);
    }, [props.isOpenOverwrite]);

    useEffect(() => {
        const updateListWidth = () => {
            if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
        };
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && event.target && !containerRef.current.contains(event.target as Node)) updateIsOpen(false);
        };

        updateListWidth();
        window.addEventListener('resize', updateListWidth);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('resize', updateListWidth);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleListScroll = () => {
            if (listRef.current) {
                if (listRef.current.scrollTop + listRef.current.clientHeight >= listRef.current.scrollHeight - SCROLL_TOLLERANCE) {
                    props.onScrollToBottom && props.onScrollToBottom();
                }
            }
        };

        listRef.current?.addEventListener('scroll', handleListScroll);
        return () => {
            listRef.current?.removeEventListener('scroll', handleListScroll);
        };
    }, [listRef.current]);

    const { refs, floatingStyles, context } = useFloating({
        placement: 'bottom-start',
        whileElementsMounted: autoUpdate,
        middleware: [flip()]
    });

    const elementsRef = useRef<Array<HTMLElement | null>>([]);
    const labelsRef = useRef<Array<string | null>>([]);

    const handleSelect = useCallback(
        (option: SingleOption<T>) => {
            props.onSelect && props.onSelect(option);
            updateIsOpen(false);
        },
        [props.isOpenOverwrite, props.onSelect]
    );

    const listNav = useListNavigation(context, {
        listRef: elementsRef,
        allowEscape: false,
        activeIndex,
        onNavigate: setActiveIndex
    });
    const click = useClick(context, {
        keyboardHandlers: false
    });
    const role = useRole(context, { role: 'listbox' });

    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([listNav, click, role]);

    const selectContext = useMemo(
        (): SelectContextValue => ({
            getItemProps,
            handleSelect
        }),
        [getItemProps, handleSelect]
    );

    return (
        <div ref={containerRef}>
            <div
                className='outline-border-default bg-background-surface inline-flex min-h-10 w-full min-w-30 cursor-pointer flex-nowrap items-center justify-start gap-2 overflow-hidden rounded-xl px-4 font-medium outline-1 outline-offset-[-1px]'
                ref={refs.setReference}
                aria-disabled={props.disabled}
                tabIndex={0}
                onClick={(e) => {
                    e.preventDefault();
                    updateIsOpen(!isOpen);
                }}
                {...getReferenceProps()}>
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        updateIsOpen(!isOpen);
                    }}
                    className={`flex-1 cursor-pointer border-0 border-none text-start ${Array.isArray(props.value) && props.value.length === 0 ? 'p-1' : 'pt-1'}`}>
                    {Array.isArray(props.value) && props.value.length === 0 && <span className='text-brand-muted opacity-90'>Select...</span>}
                    {Array.isArray(props.value) &&
                        props.value.map((value, index) => (
                            <span
                                key={index}
                                className='tag-value'>
                                <span className='tag-text'>{value}</span>
                                <span
                                    className='tag-remove'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        props.onRemove(index);
                                    }}>
                                    <LuX />
                                </span>
                            </span>
                        ))}
                </button>

                {props.isClearable && props.value && props.value.length > 0 && (
                    <span
                        className='text-brand-muted hover:text-foreground-default hover:cursor-pointer'
                        onClick={(e) => {
                            e.preventDefault();
                            props.onChange && props.onChange('');
                            props.onSelect && props.onSelect(null);
                        }}
                        id={props.id || ID + '-clear-button'}>
                        <LuX />
                    </span>
                )}
                <span
                    className='text-brand-muted hover:text-foreground-default hover:cursor-pointer'
                    aria-disabled={props.disabled}
                    onClick={(e) => {
                        e.preventDefault();
                        updateIsOpen(!isOpen);
                    }}
                    id={props.id || ID + '-dropdown-button'}>
                    {isOpen ? <LuChevronUp /> : <LuChevronDown />}
                </span>
            </div>

            <SelectContext.Provider value={selectContext}>
                {isOpen && (
                    <FloatingFocusManager
                        context={context}
                        modal={false}>
                        <div
                            className='z-index-1051'
                            ref={refs.setFloating}
                            style={floatingStyles}
                            {...getFloatingProps()}>
                            <FloatingList
                                elementsRef={elementsRef}
                                labelsRef={labelsRef}>
                                <ul
                                    ref={listRef}
                                    role='select'
                                    className='list-group no-scrollbar ignore-modal-close border-border-default mt-1 border shadow-md'
                                    style={{ minWidth: `${containerWidth}px`, maxHeight: '30vh', overflowY: 'scroll' }}>
                                    {!props.disableSearch && (
                                        <li className='list-group-item option ignore-modal-close flex flex-row items-center border-0 border-b-1'>
                                            <div className='text-brand-muted'>
                                                <ICONS.BUTTON.SEARCH className='h-5 w-5' />
                                            </div>
                                            <input
                                                ref={inputRef}
                                                id={props.id || ID + '-search-input'}
                                                aria-describedby={props.id || ID + '-dropdown-button'}
                                                className='form-control ignore-modal-close border-none text-sm outline-none'
                                                type='text'
                                                onChange={(e) => props.onChange && props.onChange(e.target.value)}
                                                placeholder={props.placeholder || 'Search...'}
                                                value={props.searchValue ?? ''}
                                                onFocus={(e) => {
                                                    updateIsOpen(true);
                                                    props.onFocus && props.onFocus(e);
                                                }}
                                                onBlur={props.onBlur}
                                                disabled={props.disabled}
                                            />
                                        </li>
                                    )}

                                    {props.optionsLoading ? <li className='list-group-item option border-none'>Loading...</li> : props.children}
                                </ul>
                            </FloatingList>
                        </div>
                    </FloatingFocusManager>
                )}
            </SelectContext.Provider>
        </div>
    );
}
